/**
 * useFeatureFlags() — small global cache of public feature flags so any
 * component can read flag state without each re-mounting hitting the
 * server. One fetch per page load, ~30s in-process TTL.
 *
 * Usage:
 *   const { flags, ready } = useFeatureFlags();
 *   if (ready && flags.pro_glow) { ... }
 */
import { useEffect, useState } from "react";
import { API } from "./api";

const TTL_MS = 30 * 1000;

let cache = { values: null, expires: 0, inflight: null };
const listeners = new Set();

function notify(values) {
  for (const fn of listeners) {
    try { fn(values); } catch { /* ignore */ }
  }
}

async function fetchFlags() {
  // Coalesce concurrent callers into a single network request
  if (cache.inflight) return cache.inflight;
  cache.inflight = (async () => {
    try {
      const r = await fetch(`${API}/feature-flags/public`, { credentials: "include" });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const data = await r.json();
      cache.values = data?.values || {};
      cache.expires = Date.now() + TTL_MS;
      notify(cache.values);
      return cache.values;
    } catch {
      // On error, fall back to empty object so callers see undefined for
      // each flag — defensive default. Don't extend TTL so we retry soon.
      cache.values = cache.values || {};
      cache.expires = Date.now() + 5000;
      return cache.values;
    } finally {
      cache.inflight = null;
    }
  })();
  return cache.inflight;
}

export function useFeatureFlags() {
  const [values, setValues] = useState(cache.values || {});
  const [ready, setReady] = useState(!!cache.values);

  useEffect(() => {
    let mounted = true;
    const sub = (v) => { if (mounted) { setValues(v); setReady(true); } };
    listeners.add(sub);

    if (!cache.values || Date.now() > cache.expires) {
      fetchFlags().then((v) => { if (mounted) { setValues(v); setReady(true); } });
    } else {
      setReady(true);
    }

    return () => {
      mounted = false;
      listeners.delete(sub);
    };
  }, []);

  return { flags: values, ready };
}

/**
 * Force-invalidate the in-process cache. Useful after an admin toggles
 * a flag in the same browser tab — call this so the page reflects the
 * new value immediately instead of waiting for TTL.
 */
export function invalidateFeatureFlags() {
  cache = { values: null, expires: 0, inflight: null };
  fetchFlags();
}
